import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type Address } from '~/types/address';
import { type UUID } from '~/types/common';
import { type CompanyType } from '~/types/company';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysCompany } from './queryKeys';

export type AllowedCompany = {
  address: Address;
  companyAccountId: UUID;
  id: UUID;
  issuerAssignedId: string;
  name: string;
  universalCommunication: {
    completeNumber: string;
    email: string;
    type: string;
  };
};

export type BulkCompaniesResponse = {
  allowedCompanies: Record<
    CompanyType,
    {
      companies: AllowedCompany[];
    }
  >;
};

/**
 * Fetches multiple companies in bulk from the API.
 * @param {UUID} companyId - Single company ID to fetch.
 * @returns {Promise<BulkCompaniesResponse>} Response containing allowed companies.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/get_bulk_allowed_companies_company_bulk_allowed_companies_get
 */
export const fetchCompaniesAllowedBulk = async (companyId: UUID) => {
  const response = await vestigasApi
    .get(ENDPOINT.COMPANY.GET_ALLOWED_BULK(companyId))
    .json<BulkCompaniesResponse>();

  return response;
};

/**
 * React Query based custom hook for getting multiple companies in bulk.
 * @param {UUID} companyId - Single company ID to fetch.
 * @param {Object} options - Additional options for the useQuery hook.
 * @returns {UseQueryResult<BulkCompaniesResponse>} The query result containing allowed companies.
 */
export const useQueryCompaniesAllowedBulk = <TSelected = BulkCompaniesResponse>(
  companyId: UUID,
  options?: Omit<
    UseQueryOptions<BulkCompaniesResponse, Error, TSelected>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<BulkCompaniesResponse, Error, TSelected>({
    enabled: Boolean(companyId),
    queryFn: async () => fetchCompaniesAllowedBulk(companyId),
    queryKey: queryKeysCompany.getAllowedBulk(companyId),
    ...options,
  });
